import { IUserFull } from '@gettactic/api';
import { useUserListByIds, useUserListPaginated } from '@gettactic/hooks';
import { useEffect, useMemo, useState, useRef } from 'react';
import { UserFilter } from '@gettactic/components/src/components/selects/OfficeFilter/UserFilter';
import { useFilterContext } from "@gettactic/components/src/controls/FilterContext/FilterContext";
import { withFilterProvider } from '../../utils/withFilterProvider';

type Props = {
  onChange: (ids: string[]) => void;
  values?: string[];
  className?: string;
  applyOnClick?: boolean;
  disabled?: boolean;
};

const SelectUsersBase = ({
  className,
  onChange,
  values = [],
  applyOnClick = false,
  disabled = false
}: Props) => {
  const [missingUsers, setMissingUsers] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const userList = useUserListPaginated(query, 10);
  const users = userList.allPages;
  const userIncludeList = useUserListByIds(missingUsers);
  const {
    filter,
    setFilter: setLocationFilter,
    resetFilter: resetLocationFilter
  } = useFilterContext();

  // Keep filter context and query state in sync
  useEffect(() => {
    if (filter !== query) {
      setQuery(filter);
    }
  }, [filter]);

  useEffect(() => {
    if (userList.isFetching) {
      return;
    }
    const missing = values.filter((x) => !users.some((y) => y.id === x));
    
    setMissingUsers(missing);
  }, [values, userList.isFetching, users]);

  const options = useMemo(() => {
    // create options and remove
    const includeList = (userIncludeList?.data ?? []).filter(
      (x) => !!x
    ) as IUserFull[];
    return users
      .concat(includeList)
      .map((x) => ({ 
        label: x.name, 
        value: x.id,
        isSelected: values.includes(x.id)
      }))
    }, [users, userIncludeList.data, values]);
    
  return (
    <div ref={containerRef} className={className}>
      <UserFilter
        applyOnClick={applyOnClick}
        disabled={disabled}
        options={options}
        defaultValue={values}
        onCancel={() => {
          setQuery('');
          resetLocationFilter();
          onChange([]);
        }}
        onInputChange={(value) => {
          setQuery(value);
          setLocationFilter(value);
        }}
        onChange={(x) => {
          const xArray = Array.isArray(x) ? x : [x];
          onChange(xArray);
        }}
        hasNextPage={userList.hasNextPage}
        fetchNextPage={userList.fetchNextPage}
        isFetchingNextPage={userList.isFetchingNextPage}
        placeholder={'Select employee'}
      />
    </div>
  );
};

// Export the component wrapped with FilterProvider
export const SelectUsers = withFilterProvider(SelectUsersBase);
